import React from 'react';
import PropTypes from 'prop-types';
import {connectToJSContext, RA_73_STERILIZE_HTML_STRING} from '../utils.jsx';
import {makeFooterSections, setAriaLabelAttributeForExternalLinks} from '../helpers';
import {Utils} from 'poly-js-utils';
import _ from 'underscore';
import SocialIconsList from '../social_icons/index.jsx';
import { GENERAL } from '../constants';
import LazyImg from '../lazy_img/index.jsx';



const BrokerCheckLink = () => (
    <div className="brokercheck-link text-left col-xs-12">
        <em>
            {GENERAL.BROKER_CHECK.TEXT}
            {' '}{/* Non-breaking whitespace */}
            <a
                href={GENERAL.BROKER_CHECK.ANCHOR_URL}
                title={GENERAL.BROKER_CHECK.ANCHOR_TITLE}
            >
                {GENERAL.BROKER_CHECK.ANCHOR_TEXT}
            </a>
        </em>
    </div>
);

const StateDisclaimers = ({disclaimers_list, baseLink}) => (
    <div className="state-disclaimers text-left col-xs-12">
        <a href={baseLink + 'disclaimers'}>{disclaimers_list.join(', ')}</a>
    </div>
);

const FooterSections = (props) => {
    const externalWindowLinkAriaText = props.labels.externalWindowLinkAriaText;
    const replaceDescription = (desc) => {
        let formattedDesc = desc;

        formattedDesc = setAriaLabelAttributeForExternalLinks(
            formattedDesc,
            externalWindowLinkAriaText
        );

        return formattedDesc;
    };

    return (
        <div className="wrapper footer-content" style={{backgroundColor: props.backgroundColor}}>
            <div className="container">
                <div className="row">
                    <div className="text-right col-xs-12">
                        <SocialIconsList networks={props.networks} />
                    </div>
                    {props.footerSections.map((section, idx) => (
                        <div className="text-left col-xs-12" key={idx}>
                            {section.header && <h2>{section.header}</h2>}
                            <p
                                dangerouslySetInnerHTML={{
                                    __html: RA_73_STERILIZE_HTML_STRING(replaceDescription(section.content))
                                }}
                            />
                        </div>
                    ))}
                    {props.is_disclaimers_displayed &&
                        <StateDisclaimers
                            disclaimers_page_title={props.disclaimers_page_title}
                            disclaimers_list={props.disclaimers_list}
                            baseLink={props.baseLink}
                        />
                    }
                    {props.includeBrokercheckLink && <BrokerCheckLink />}
                </div>
            </div>
        </div>
    );
};

const Footer = (props) => (
    <div className="hs-footer" role="contentinfo">
        {!!props.footerSections.length &&
            <FooterSections {...props}
                classPrefix="footer"
            />
        }
        <div className="wrapper copyright-text" style={{backgroundColor: props.backgroundCopyrightColor}}>
            <div className="container">
                <div className="row">
                    {props.footerLogo &&
                    <div className="text-left col-sm-offset-0 col-sm-3">
                        <a
                            className="hs-brand-logo"
                            href={(props.logoLink && props.logoLink.url) || props.baseLink}
                            target={props.logoLink && props.logoLink.open_in_new_window ? "_blank" : "_self"}
                            rel="noopener"
                        >
                            <LazyImg src={props.footerLogo} alt={props.footerLogoAlt || ""} />
                        </a>
                    </div>
                    }
                    <div className={"col-sm-offset-0 col-sm-9 " + (props.footerLogo ? "text-right" : "")}>
                        <p
                            className="copyright"
                            dangerouslySetInnerHTML={{
                                __html: RA_73_STERILIZE_HTML_STRING(props.copyrightString)
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
);

Footer.propTypes = {
    backgroundColor: PropTypes.string,
    backgroundCopyrightColor: PropTypes.string,
    baseLink: PropTypes.string.isRequired,
    copyrightString: PropTypes.string.isRequired,
    footerLogoAlt: PropTypes.string.isRequired,
    logoLink: PropTypes.shape({
        url: PropTypes.string,
        open_in_new_window: PropTypes.bool,
        is_external: PropTypes.bool
    }),
    footerLogo: PropTypes.string,
    footerSections: PropTypes.arrayOf(
        PropTypes.shape({
            header: PropTypes.string,
            content: PropTypes.string.isRequired
        })
    ).isRequired,
    networks: PropTypes.arrayOf(
        PropTypes.shape({
            type: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    ),
    networksScreenReaderText: PropTypes.string,
    includeBrokercheckLink: PropTypes.bool,
    disclaimers_page_title: PropTypes.string,
    disclaimers_list: PropTypes.array,
    is_disclaimers_displayed: PropTypes.bool
};

const mapStateToProps = (state) => {
    const profile = state.profile;
    const name = profile.name || `${profile.first_name} ${profile.last_name}`;
    const footer = state.theme_data.footer;

    return {
        backgroundColor: footer.footer_background_color,
        backgroundCopyrightColor: footer.footer_background_copyright_color,
        baseLink: state.base_link,
        copyrightString: footer.footer_copyright_string,
        footerLogoAlt: profile.footer_logo_alt || footer.footer_logo_alt,
        logoLink: {
            url: (state.profile.footer_logo_url && state.profile.footer_logo_url.url) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.url),
            open_in_new_window: Utils.selectBoxIsTrue(
                (state.profile.footer_logo_url && state.profile.footer_logo_url.open_in_new_window) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.open_in_new_window)
            ),
            is_external: Utils.selectBoxIsTrue(
                (state.profile.footer_logo_url && state.profile.footer_logo_url.is_external) ||
                (state.theme_data.nav.logo_url && state.theme_data.nav.logo_url.is_external)
            )
        },
        footerLogo: profile.footer_logo || footer.footer_logo,
        footerSections: makeFooterSections(state),
        includeBrokercheckLink: footer.include_brokercheck_link[0] === 'true',
        networks:_.map(state.theme_data.footer_social_icons, (network, key) => ({
            type: key,
            url: network.url
        })),
        networksScreenReaderText: `for ${name}`,
        disclaimers_page_title: state.theme_data.disclaimers_page ?
            state.theme_data.disclaimers_page.header_title : null,
        disclaimers_list: state.profile.disclaimers_list || [],
        is_disclaimers_displayed: Utils.selectBoxIsTrue(
            state.theme_data.disclaimers_page &&
            state.theme_data.disclaimers_page.header_title &&
            state.profile.is_disclaimer_displayed_in_footer
        ),
        labels: state.theme_data.labels
    };
};

export default connectToJSContext(mapStateToProps)(Footer);



// WEBPACK FOOTER //
// ./components/footer/index.jsx